// The Pulse: Rounds' living brand element, an ECG line with moods. Original to Rounds (nothing borrowed).
//
// steady  sinus rhythm (the lobby, the wordmark)
// racing  tachycardia as the clock runs down
// spike   a celebratory burst when you are right, sparks off the head
// jolt    a sag, a defib-style shock and the whole line jolts, then recovery
// flat    a soft flatline when you miss; the head dims
// crown   traces a crown with jewels at the podium
//
// Drawn procedurally by ./pulse/engine. While it plays, only transform and opacity change (Web Animations API, so the
// compositor runs it at 60 fps even when the main thread is busy). The path itself is rebuilt every few seconds or
// when the mood changes. Reduced motion, server rendering and browsers without WAAPI get a still frame of the mood.
// Layout: the Pulse paints inside its own width (the scrolling strip, the head and the sparks are clipped at the left
// and right edges), so it never widens a phone page; spikes and the crown may rise above the box.
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import {
  HALO, HEAD_R, Tape, accentBeat, buildStrip, moodProgram, pose, scaleFor, speedFor, strokeFor,
  type Geometry, type PulseMood, type Scale, type Strip,
} from './pulse/engine';
import { canAnimate, timelineNow, useReducedMotion } from '@/motion/reduced';

export type { PulseMood } from './pulse/engine';

export interface PulseProps {
  mood?: PulseMood;
  /** px, or any CSS length ('100%', '12em'): then the Pulse measures itself. */
  width?: number | string;
  height?: number | string;
  className?: string;
  /** Line colour (default currentColor). */
  color?: string;
  strokeWidth?: number;
  /** false draws a still frame of the mood. */
  animated?: boolean;
  /** A soft wide halo under the line (best on the dark monitor and on teal). */
  glow?: boolean;
  /** Fade the old trace out on the left, like phosphor. */
  fade?: boolean;
  /** Draw the beam head where the line is being written. */
  head?: boolean;
  /** Override the rate for steady (72) and racing (162). */
  bpm?: number;
  /** Change this value to make the Pulse tick once (an accented beat), e.g. on lock-in. */
  tick?: unknown;
  /** Accessible name. Without it the Pulse is decorative and hidden from screen readers. */
  label?: string;
  seed?: number;
}

const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
const SVG_NS = 'http://www.w3.org/2000/svg';

export function Pulse({
  mood = 'steady', width = 120, height = 36, className = '', color = 'currentColor', strokeWidth, animated = true,
  glow = false, fade = true, head = true, bpm, tick, label, seed = 1,
}: PulseProps) {
  const rootRef = useRef<HTMLSpanElement>(null);
  const [measured, setMeasured] = useState<{ w: number; h: number }>();
  const fixed = typeof width === 'number' && typeof height === 'number';
  const W = typeof width === 'number' ? width : measured?.w ?? 0;
  const H = typeof height === 'number' ? height : measured?.h ?? 0;
  const reduced = useReducedMotion();
  const [wa] = useState(canAnimate);
  const live = animated && !reduced && wa && W > 0 && H > 0;

  // Measure when the size is given as a CSS length.
  useIsoLayoutEffect(() => {
    if (fixed || !rootRef.current) return;
    const el = rootRef.current;
    const read = () => { const r = el.getBoundingClientRect(); setMeasured((m) => (m && Math.abs(m.w - r.width) < 0.5 && Math.abs(m.h - r.height) < 0.5 ? m : { w: r.width, h: r.height })); };
    read();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(read); ro.observe(el);
    return () => ro.disconnect();
  }, [fixed]);

  const stroke = strokeWidth ?? strokeFor(H || 36);
  const still = useMemo(() => (W > 0 && H > 0 ? pose(mood, W, H, stroke, seed, bpm) : undefined), [mood, W, H, stroke, seed, bpm]);
  const g = still?.g;
  const headX = g ? (head ? g.headX : W) : 0;
  const fadeLen = fade ? Math.min(W * 0.34, 160) : 0;
  const mask = `linear-gradient(to right, transparent 0px, #000 ${fadeLen}px, #000 ${headX}px, transparent ${headX + 0.5}px)`;
  const r = stroke * HEAD_R;

  // overflow-x: clip (with y visible) keeps everything inside the width; older browsers ignore it and still clip the
  // strip itself (viewStyle), the only part that could reach far past the edge.
  // A fixed-size Pulse keeps its size in a flex row; one sized in % or em may shrink with its row (it measures itself).
  const rootStyle: CSSProperties = { display: 'inline-block', position: 'relative', width, height, color, verticalAlign: 'middle', flexShrink: fixed ? 0 : 1, minWidth: 0, overflowX: 'clip' };
  const a11y = label ? { role: 'img' as const, 'aria-label': label } : { 'aria-hidden': true as const };

  return (
    <span ref={rootRef} className={`rd-pulse ${className}`} style={rootStyle} data-mood={mood} {...a11y}>
      {still && g && (live
        ? <LiveTrace key={`${W}x${H}:${stroke}:${seed}`} mood={mood} W={W} H={H} g={{ ...g, headX }} stroke={stroke} glow={glow} head={head} bpm={bpm} tick={tick} seed={seed} mask={mask} r={r} rootRef={rootRef} initial={still.strip} initialHeadY={still.headY} />
        : <StillTrace key={mood} strip={still.strip} g={g} headX={headX} headY={still.headY} headOpacity={still.headOpacity} stroke={stroke} glow={glow} head={head} mask={mask} r={r} fadeIn={reduced} />)}
    </span>
  );
}

function viewStyle(g: Geometry, mask: string): CSSProperties {
  return { position: 'absolute', left: 0, right: 0, top: -g.pad, bottom: -g.pad, overflow: 'hidden', WebkitMaskImage: mask, maskImage: mask, WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat', pointerEvents: 'none' };
}

/** The beam head: a soft halo of constant size and the dot, which alone swells on the big moments (so the halo is
 *  never cut by the right edge). The outer span moves; the dot scales. */
function HeadDot({ r, style, innerRef, dotRef }: { r: number; style?: CSSProperties; innerRef?: React.Ref<HTMLSpanElement>; dotRef?: React.Ref<HTMLSpanElement> }) {
  return (
    <span ref={innerRef} className="rd-pulse-head" style={{ position: 'absolute', left: 0, top: 0, width: 0, height: 0, pointerEvents: 'none', ...style }}>
      <span style={{ position: 'absolute', left: -r * HALO, top: -r * HALO, width: r * HALO * 2, height: r * HALO * 2, borderRadius: '50%', background: 'currentColor', opacity: 0.16 }} />
      <span ref={dotRef} style={{ position: 'absolute', left: -r, top: -r, width: r * 2, height: r * 2, borderRadius: '50%', background: 'currentColor', willChange: dotRef ? 'transform' : undefined }} />
    </span>
  );
}

function StillTrace({ strip, g, headX, headY, headOpacity, stroke, glow, head, mask, r, fadeIn }: {
  strip: Strip; g: Geometry; headX: number; headY: number; headOpacity: number; stroke: number; glow: boolean; head: boolean; mask: string; r: number; fadeIn: boolean;
}) {
  return (
    <>
      <span style={viewStyle(g, mask)} className={fadeIn ? 'rd-fade-in' : undefined}>
        <svg width={strip.width} height={strip.height} style={{ position: 'absolute', left: 0, top: 0, overflow: 'visible' }} aria-hidden="true" focusable="false">
          {glow && <path d={strip.d} fill="none" stroke="currentColor" strokeOpacity={0.18} strokeWidth={stroke * 3.4} strokeLinecap="round" strokeLinejoin="round" />}
          <path d={strip.d} fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" />
          {strip.lines.map((l, i) => <line key={i} x1={l.x1} x2={l.x2} y1={l.y} y2={l.y} stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" />)}
          {strip.dots.map((d, i) => <circle key={i} cx={d.cx} cy={d.cy} r={d.r} fill="currentColor" />)}
        </svg>
      </span>
      {head && <HeadDot r={r} style={{ transform: `translate3d(${headX}px, ${headY}px, 0)`, opacity: headOpacity }} />}
    </>
  );
}

interface LiveProps {
  mood: PulseMood; W: number; H: number; g: Geometry; stroke: number; glow: boolean; head: boolean; bpm?: number; tick: unknown; seed: number;
  mask: string; r: number; rootRef: React.RefObject<HTMLSpanElement | null>; initial: Strip; initialHeadY: number;
}

/** The playing Pulse. All per-frame motion is WAAPI on transform and opacity; React renders it once. */
function LiveTrace({ mood, W, H, g, stroke, glow, head, bpm, tick, seed, mask, r, rootRef, initial: initialProp, initialHeadY: initialHeadYProp }: LiveProps) {
  // The first frame is captured once: after mount the engine owns the path and the head, and React must never
  // write the still pose back over them when the mood changes.
  const [{ initial, initialHeadY }] = useState(() => ({ initial: initialProp, initialHeadY: initialHeadYProp }));
  const stripRef = useRef<HTMLSpanElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const traceRef = useRef<SVGPathElement>(null);
  const glowRef = useRef<SVGPathElement>(null);
  const extrasRef = useRef<SVGGElement>(null);
  const headRef = useRef<HTMLSpanElement>(null);
  const dotRef = useRef<HTMLSpanElement>(null);
  const sparksRef = useRef<HTMLSpanElement>(null);
  const layerRef = useRef<HTMLSpanElement>(null);
  const engine = useRef<Engine | null>(null);

  // Start once per size; stop on unmount.
  useIsoLayoutEffect(() => {
    // The jolt shakes the inner layer; the root (clipped at its edges) never moves, so the page never shifts.
    const els = { strip: stripRef.current!, svg: svgRef.current!, trace: traceRef.current!, glow: glowRef.current, extras: extrasRef.current!, head: headRef.current, dot: dotRef.current, sparks: sparksRef.current!, layer: () => layerRef.current ?? rootRef.current };
    const e = new Engine(els, { W, H, g, stroke, seed, bpm, r });
    engine.current = e;
    e.start(mood);
    return () => { e.stop(); engine.current = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const firstMood = useRef(true);
  useEffect(() => { if (firstMood.current) { firstMood.current = false; return; } engine.current?.setMood(mood, bpm); }, [mood, bpm]);
  const firstTick = useRef(true);
  useEffect(() => { if (firstTick.current) { firstTick.current = false; return; } engine.current?.tick(); }, [tick]);

  return (
    <span ref={layerRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      <span style={viewStyle(g, mask)}>
        {/* The strip scrolls as an HTML box (always composited); the SVG inside is painted once per rebuild. */}
        <span ref={stripRef} style={{ position: 'absolute', left: 0, top: 0, display: 'block', willChange: 'transform' }}>
          <svg ref={svgRef} width={initial.width} height={initial.height} style={{ display: 'block', overflow: 'visible' }} aria-hidden="true" focusable="false">
            <path ref={glowRef} d={glow ? initial.d : ''} fill="none" stroke="currentColor" strokeOpacity={0.18} strokeWidth={stroke * 3.4} strokeLinecap="round" strokeLinejoin="round" display={glow ? undefined : 'none'} />
            <path ref={traceRef} d={initial.d} fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" />
            <g ref={extrasRef} />
          </svg>
        </span>
      </span>
      {head && <HeadDot r={r} innerRef={headRef} dotRef={dotRef} style={{ transform: `translate3d(${g.headX}px, ${initialHeadY}px, 0)`, willChange: 'transform, opacity' }} />}
      <span ref={sparksRef} style={{ position: 'absolute', left: 0, top: 0, width: 0, height: 0, pointerEvents: 'none' }} />
    </span>
  );
}

interface Els { strip: HTMLSpanElement; svg: SVGSVGElement; trace: SVGPathElement; glow: SVGPathElement | null; extras: SVGGElement; head: HTMLSpanElement | null; dot: HTMLSpanElement | null; sparks: HTMLSpanElement; layer: () => HTMLElement | null }
interface Opts { W: number; H: number; g: Geometry; stroke: number; seed: number; bpm?: number; r: number }

/** Drives one live Pulse: owns the tape, cuts strips and keeps the animations seamless across rebuilds. */
class Engine {
  private sc: Scale; private tape!: Tape; private mood: PulseMood = 'steady'; private bpm?: number;
  private anims: Animation[] = []; private pending: Animation[] = [];
  private timer = 0; private stripT0 = 0; private stripStart = 0; private stripDur = 0;
  private stopped = false;
  private onVis = () => { if (document.visibilityState === 'visible') this.rebuild(); };

  constructor(private els: Els, private o: Opts) { this.sc = scaleFor(o.W, o.H); this.bpm = o.bpm; }

  start(mood: PulseMood) {
    this.mood = mood;
    const v = speedFor(this.sc);
    // A little steady history so the window is full at first sight, then the mood starts at the head.
    this.tape = new Tape(moodProgram('steady', this.sc, this.o.seed, this.bpm, this.o.H));
    const lead = (this.o.g.headX + 4) / v + 0.4;
    this.tape.ensureT(lead);
    if (mood !== 'steady') this.tape.rewrite(lead, moodProgram(mood, this.sc, this.o.seed, this.bpm, this.o.H), [], this.sc);
    this.cut(lead, timelineNow());
    document.addEventListener('visibilitychange', this.onVis);
  }
  stop() {
    this.stopped = true;
    window.clearTimeout(this.timer);
    document.removeEventListener('visibilitychange', this.onVis);
    for (const a of [...this.anims, ...this.pending]) a.cancel();
    this.anims = []; this.pending = [];
    this.els.sparks.replaceChildren();
  }
  /** Tape time now, read from the same clock the animations run on. */
  private now(): { tau: number; T: number } {
    const T = timelineNow();
    return { tau: this.stripT0 + Math.min(this.stripDur, Math.max(0, (T - this.stripStart) / 1000)), T };
  }
  setMood(mood: PulseMood, bpm?: number) {
    if (this.stopped) return;
    const { tau, T } = this.now();
    this.mood = mood; this.bpm = bpm;
    this.tape.rewrite(tau, moodProgram(mood, this.sc, this.o.seed, bpm, this.o.H), [], this.sc);
    this.cut(tau, T);
  }
  tick() {
    if (this.stopped) return;
    const { tau, T } = this.now();
    const p = moodProgram(this.mood, this.sc, this.o.seed, this.bpm, this.o.H);
    this.tape.rewrite(tau, { intro: [], loop: p.loop }, [accentBeat(this.sc)], this.sc);
    this.cut(tau, T);
  }
  private rebuild() { if (!this.stopped) { const { tau, T } = this.now(); this.cut(tau, T); } }

  /** Cuts a strip starting at tape time tau and starts it at timeline time T, exactly in phase with the old one. */
  private cut(tau: number, T: number) {
    const { W, g, stroke } = this.o;
    const v = speedFor(this.sc);
    const dur = Math.min(8, Math.max(2.5, (1.6 * W) / v));
    const strip = buildStrip(this.tape, tau, tau + dur, g, stroke);
    this.paint(strip);
    const ms = dur * 1000;
    const scroll = this.els.strip.animate(strip.scroll.map((f) => ({ offset: f.offset, transform: `translate3d(${f.x}px,0,0)` })), { duration: ms, fill: 'forwards', easing: 'linear' });
    scroll.startTime = T;
    const next: Animation[] = [scroll];
    if (this.els.head) {
      const hx = g.headX;
      const opts: KeyframeAnimationOptions = { duration: ms, fill: 'forwards', easing: 'linear' };
      const head = this.els.head.animate(strip.head.map((f) => ({ offset: f.offset, transform: `translate3d(${hx}px,${f.y}px,0)`, opacity: f.o })), opts);
      head.startTime = T;
      next.push(head);
      if (this.els.dot) {
        const dot = this.els.dot.animate(strip.head.map((f) => ({ offset: f.offset, transform: `scale(${f.s})` })), opts);
        dot.startTime = T;
        next.push(dot);
      }
    }
    for (const a of this.anims) a.cancel();
    for (const a of this.pending) if (a.playState !== 'finished') a.cancel();
    this.anims = next; this.pending = [];
    for (const s of strip.shakes) this.shake(T + s * 1000);
    for (const s of strip.sparks) this.spark(T + s * 1000, g.baseline - this.tape.at(tau + s).y * g.amp);
    this.stripT0 = tau; this.stripStart = T; this.stripDur = dur;
    this.tape.prune(strip.originX - 8);
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.rebuild(), Math.max(400, ms - 700));
  }
  private paint(strip: Strip) {
    const { svg, trace, glow, extras } = this.els;
    svg.setAttribute('width', String(strip.width)); svg.setAttribute('height', String(strip.height));
    svg.setAttribute('data-origin', String(strip.originX)); // tape x at the strip's left edge (for diagnostics)
    trace.setAttribute('d', strip.d);
    if (glow && glow.getAttribute('display') !== 'none') glow.setAttribute('d', strip.d);
    const kids: SVGElement[] = [];
    for (const l of strip.lines) {
      const el = document.createElementNS(SVG_NS, 'line');
      el.setAttribute('x1', String(l.x1)); el.setAttribute('x2', String(l.x2)); el.setAttribute('y1', String(l.y)); el.setAttribute('y2', String(l.y));
      el.setAttribute('stroke', 'currentColor'); el.setAttribute('stroke-width', String(this.o.stroke)); el.setAttribute('stroke-linecap', 'round');
      kids.push(el);
    }
    for (const d of strip.dots) {
      const el = document.createElementNS(SVG_NS, 'circle');
      el.setAttribute('cx', String(d.cx)); el.setAttribute('cy', String(d.cy)); el.setAttribute('r', String(d.r)); el.setAttribute('fill', 'currentColor');
      kids.push(el);
    }
    extras.replaceChildren(...kids);
  }
  /** The defib jolt: the whole trace kicks sideways when the shock reaches the head. */
  private shake(at: number) {
    const root = this.els.layer();
    if (!root) return;
    const a = Math.max(2, this.o.H * 0.09);
    const anim = root.animate([
      { transform: 'translate3d(0,0,0)' }, { transform: `translate3d(${-a}px,${a * 0.2}px,0)`, offset: 0.1 }, { transform: `translate3d(${a * 0.8}px,${-a * 0.15}px,0)`, offset: 0.26 },
      { transform: `translate3d(${-a * 0.45}px,0,0)`, offset: 0.46 }, { transform: `translate3d(${a * 0.2}px,0,0)`, offset: 0.68 }, { transform: 'translate3d(0,0,0)' },
    ], { duration: 420, easing: 'ease-out' });
    anim.startTime = at;
    this.pending.push(anim);
  }
  /** Sparks thrown from the head on a celebration or as the crown's centre point is drawn: a fan that leans back over
   *  the trace (up and to the left), since the right edge is where the Pulse ends. */
  private spark(at: number, y: number) {
    const { sparks } = this.els;
    const n = 9; const size = Math.max(2, this.o.stroke * 0.95);
    for (let i = 0; i < n; i++) {
      const dot = document.createElement('span');
      dot.style.cssText = `position:absolute;left:${this.o.g.headX - size / 2}px;top:${y - size / 2}px;width:${size}px;height:${size}px;border-radius:50%;background:currentColor;opacity:0;will-change:transform,opacity`;
      sparks.appendChild(dot);
      const ang = -Math.PI / 2 - 0.42 + (i - (n - 1) / 2) * 0.3 + Math.sin(i * 12.9898) * 0.1;
      const dist = this.o.H * (0.55 + 0.35 * Math.abs(Math.sin(i * 78.233)));
      const dx = Math.cos(ang) * dist - this.o.H * 0.3; const dy = Math.sin(ang) * dist;
      const anim = dot.animate([
        { transform: 'translate3d(0,0,0) scale(1)', opacity: 1 },
        { transform: `translate3d(${dx * 0.7}px,${dy * 0.7}px,0) scale(0.9)`, opacity: 1, offset: 0.45 },
        { transform: `translate3d(${dx}px,${dy + this.o.H * 0.25}px,0) scale(0.2)`, opacity: 0 },
      ], { duration: 620 + i * 25, easing: 'cubic-bezier(.2,.7,.3,1)', fill: 'forwards' });
      anim.startTime = at;
      anim.onfinish = () => dot.remove(); anim.oncancel = () => dot.remove();
      this.pending.push(anim);
    }
  }
}
